import {
  memo,
  useReducer,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import '../styles/deposit-modal.css';
import PaymentSubModal from './PaymentSubModal';
import StripePaymentForm from './StripePaymentForm';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const STRIPE_APPEARANCE = {
  theme: 'night',
  variables: {
    colorPrimary: '#ffd700',
    colorBackground: '#0e0e20',
    colorText: '#ffffff',
    colorTextSecondary: 'rgba(255,255,255,0.5)',
    colorTextPlaceholder: 'rgba(255,255,255,0.2)',
    colorDanger: '#ff7875',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    fontSizeBase: '14px',
    borderRadius: '10px',
    spacingUnit: '4px',
    spacingGridRow: '16px',
  },
  rules: {
    '.Input': {
      border: '1.5px solid rgba(255,215,0,0.14)',
      backgroundColor: 'rgba(255,255,255,0.03)',
      boxShadow: 'none',
      color: '#ffffff',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      padding: '10px 12px',
    },
    '.Input:focus': {
      border: '1.5px solid rgba(255,215,0,0.55)',
      backgroundColor: 'rgba(255,215,0,0.04)',
      boxShadow: '0 0 0 3px rgba(255,215,0,0.08)',
    },
    '.Input--invalid': {
      border: '1.5px solid rgba(255,120,117,0.6)',
      boxShadow: '0 0 0 3px rgba(255,120,117,0.06)',
    },
    '.Label': {
      color: 'rgba(255,255,255,0.4)',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '6px',
    },
    '.Tab': {
      border: '1px solid rgba(255,215,0,0.1)',
      backgroundColor: 'rgba(255,255,255,0.02)',
      color: 'rgba(255,255,255,0.45)',
      borderRadius: '8px',
      boxShadow: 'none',
    },
    '.Tab--selected': {
      border: '1px solid rgba(255,215,0,0.4)',
      backgroundColor: 'rgba(255,215,0,0.07)',
      color: '#ffffff',
      boxShadow: '0 0 12px rgba(255,215,0,0.08)',
    },
    '.Tab--selected .TabIcon': { fill: '#ffd700' },
    '.Tab:hover': {
      backgroundColor: 'rgba(255,215,0,0.04)',
      color: 'rgba(255,255,255,0.75)',
      border: '1px solid rgba(255,215,0,0.2)',
    },
    '.TabLabel': { color: 'inherit', fontWeight: '600' },
    '.TabIcon': { fill: 'rgba(255,255,255,0.4)' },
    '.Error': { color: '#ff7875', fontSize: '12px' },
    '.TermsText': { color: 'rgba(255,255,255,0.25)' },
    '.RedirectText': { color: 'rgba(255,255,255,0.3)' },
  },
};

const UID = new URLSearchParams(window.location.search).get('uid') || '';

const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£', JPY: '¥', CAD: '$', AUD: '$', CHF: 'Fr' };

const INITIAL_STATE = {
  form: { amount: '' },
  currency: 'EUR',
  returnUrl: '',
  transactionReference: '',
  customerFullname: '',
  customerEmail: '',
  deetsLoading: true,
  deetsError: null,
  clientSecret: null,
  submitting: false,
  validating: false,
  submitError: null,
  showSubModal: false,
  stripeError: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'DEETS_READY':
      return {
        ...state,
        deetsLoading: false,
        deetsError: null,
        form: { ...state.form, amount: action.payload.amount },
        currency: action.payload.currency,
        returnUrl: action.payload.returnUrl || state.returnUrl,
        transactionReference: action.payload.transactionReference,
        customerFullname: action.payload.customerFullname,
        customerEmail: action.payload.customerEmail,
      };
    case 'DEETS_ERROR':
      return { ...state, deetsLoading: false, deetsError: action.payload };
    case 'SUBMIT_START':
      return { ...state, submitting: true, submitError: null, stripeError: null };
    case 'SUBMIT_READY':
      return { ...state, submitting: false, clientSecret: action.payload, showSubModal: false };
    case 'VALIDATING':
      return { ...state, validating: action.payload, stripeError: null };
    case 'OPEN_SUBMODAL':
      return { ...state, validating: false, showSubModal: true };
    case 'SUBMIT_ERROR':
      return { ...state, submitting: false, submitError: action.payload };
    case 'STRIPE_ERROR':
      return { ...state, stripeError: action.payload };
    case 'CLOSE_SUBMODAL':
      return { ...state, validating: false, showSubModal: false, stripeError: null };
    case 'PAYMENT_SUCCESS':
      return { ...INITIAL_STATE, deetsLoading: false, currency: state.currency, returnUrl: state.returnUrl, form: { amount: state.form.amount } };
    default:
      return state;
  }
}

const StripeSection = memo(function StripeSection({
  elementsOptions,
  stripeFormRef,
  onPaymentError,
  returnUrl,
  customerFullname,
  customerEmail,
}) {
  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <StripePaymentForm
        ref={stripeFormRef}
        onPaymentError={onPaymentError}
        returnUrl={returnUrl}
        customerFullname={customerFullname}
        customerEmail={customerEmail}
      />
    </Elements>
  );
});

async function fetchDeets(uid) {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-deets.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
    },
    body: JSON.stringify({ uid }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to load payment details');
  }

  return data;
}

async function createPaymentIntent(amount, currency, transactionReference) {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/create-react.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
    },
    body: JSON.stringify({ amount, currency, transactionReference }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create PaymentIntent');
  }

  return data.clientSecret;
}

function DepositModal({ isOpen }) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const stripeFormRef = useRef(null);
  // Locked at PaymentIntent creation time — never changes after that point.
  const lockedReturnUrlRef = useRef(null);
  const stripeErrorHandlerRef = useRef(null);

  useEffect(() => {
    fetchDeets(UID)
      .then((data) => dispatch({
        type: 'DEETS_READY',
        payload: {
          amount: String(data.amount ?? ''),
          currency: (data.currency || 'EUR').toUpperCase(),
          returnUrl: data.success_url || data.return_url || data.returnUrl || '',
          transactionReference: data.transactionReference || '',
          customerFullname: data.customerFullname || '',
          customerEmail: data.customerEmail || '',
        },
      }))
      .catch(() => {
        dispatch({ type: 'DEETS_ERROR', payload: 'Unable to load payment details. Please try again.' });
      });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (state.clientSecret) return;

    const parsed = parseFloat(state.form.amount);
    if (!parsed || parsed <= 0) {
      dispatch({ type: 'SUBMIT_ERROR', payload: 'Please enter a valid deposit amount.' });
      return;
    }

    dispatch({ type: 'SUBMIT_START' });

    try {
      const clientSecret = await createPaymentIntent(Math.round(parsed * 100), state.currency.toLowerCase(), state.transactionReference);
      lockedReturnUrlRef.current = state.returnUrl;
      dispatch({ type: 'SUBMIT_READY', payload: clientSecret });
    } catch {
      dispatch({
        type: 'SUBMIT_ERROR',
        payload: 'Unable to initialise payment. Check your connection and try again.',
      });
    }
  }, [state.clientSecret, state.form.amount, state.currency, state.transactionReference, state.returnUrl]);

  const handleStripeError = useCallback((msg) => {
    if (stripeErrorHandlerRef.current) {
      stripeErrorHandlerRef.current(msg);
    } else {
      dispatch({ type: 'STRIPE_ERROR', payload: msg });
    }
  }, []);

  const handleStripeConfirm = useCallback(async (onError) => {
    if (!stripeFormRef.current) return null;
    stripeErrorHandlerRef.current = onError || null;
    const result = await stripeFormRef.current.confirmPayment();
    stripeErrorHandlerRef.current = null;
    return result;
  }, []);

  const handleCloseSubModal = useCallback(() => {
    dispatch({ type: 'CLOSE_SUBMODAL' });
  }, []);

  const handleOpenSubModal = useCallback(async () => {
    if (!stripeFormRef.current) return;
    dispatch({ type: 'VALIDATING', payload: true });
    try {
      const valid = await stripeFormRef.current.validateFields();
      if (!valid) {
        dispatch({ type: 'VALIDATING', payload: false });
        return;
      }
      dispatch({ type: 'OPEN_SUBMODAL' });
    } catch {
      dispatch({ type: 'VALIDATING', payload: false });
    }
  }, []);

  const elementsOptions = useMemo(() => {
    if (!state.clientSecret) return null;
    return { clientSecret: state.clientSecret, appearance: STRIPE_APPEARANCE };
  }, [state.clientSecret]);

  const paymentData = useMemo(
    () => ({ amount: state.form.amount, currency: state.currency }),
    [state.form.amount, state.currency]
  );

  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Deposit funds"
    >
      <div className="modal-container">
        <aside className="modal-sidebar">
          <div className="sidebar-aurora">
            <div className="sidebar-aurora-1" />
            <div className="sidebar-aurora-2" />
          </div>
          <div className="sidebar-content">
            <div className="sidebar-brand">
              <div className="sidebar-logo-wrap">
                <img src="/icon.png" alt="Cornetto Pro" className="sidebar-logo" />
              </div>
              <h2 className="sidebar-company">Cornetto Pro</h2>
            </div>

            <div className="sidebar-divider" />

            <ul className="sidebar-features">
              <li className="sidebar-feature">
                <span className="sf-icon">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
                <span className="sf-text">256-bit encrypted</span>
              </li>
              <li className="sidebar-feature">
                <span className="sf-icon">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
                <span className="sf-text">Instant processing</span>
              </li>
              <li className="sidebar-feature">
                <span className="sf-icon">
                  <svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ffd700" strokeWidth="1.5" /><path d="M12 6v6l4 2" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" /></svg>
                </span>
                <span className="sf-text">24/7 availability</span>
              </li>
              <li className="sidebar-feature">
                <span className="sf-icon">
                  <svg viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="#ffd700" strokeWidth="1.5" /><path d="M2 10H22" stroke="#ffd700" strokeWidth="1.5" /></svg>
                </span>
                <span className="sf-text">All major cards accepted</span>
              </li>
            </ul>

            <div className="sidebar-cards">
              <svg viewBox="0 0 48 32" fill="none" className="sidebar-card-badge" aria-label="Visa"><rect width="48" height="32" rx="4" fill="#1434CB" /><text x="24" y="22" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="Arial">VISA</text></svg>
              <svg viewBox="0 0 48 32" fill="none" className="sidebar-card-badge" aria-label="Mastercard"><rect width="48" height="32" rx="4" fill="#1a1a1a" /><circle cx="18" cy="16" r="9" fill="#EB001B" /><circle cx="30" cy="16" r="9" fill="#FFB81C" /><ellipse cx="24" cy="16" rx="3.5" ry="9" fill="#FF5F00" /></svg>
            </div>

            <div className="sidebar-secured">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 1L2 3.5V8c0 3.5 2.5 6 6 7 3.5-1 6-3.5 6-7V3.5L8 1z" fill="rgba(255,215,0,0.15)" stroke="#ffd700" strokeWidth="1" /><path d="M5 8l2 2 4-4" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span>Secured by Stripe</span>
            </div>
          </div>
        </aside>

        <div className="modal-form-section">
          <header className="form-header">
            <h2>Add Funds to Your Account</h2>
          </header>

          {state.deetsError && (
            <p className="submit-message error" role="alert">{state.deetsError}</p>
          )}

          <form className="deposit-form" onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="dm-amount">Deposit Amount ({state.currency})</label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol" aria-hidden="true">{CURRENCY_SYMBOLS[state.currency] ?? state.currency}</span>
                <input
                  id="dm-amount"
                  type="text"
                  inputMode="decimal"
                  name="amount"
                  placeholder="10.00"
                  value={state.form.amount}
                  readOnly
                  autoComplete="off"
                />
              </div>
            </div>

            {state.submitError && (
              <p className="submit-message error" role="alert">{state.submitError}</p>
            )}

            {!state.clientSecret && (
              <button
                type="submit"
                className={`deposit-button${state.submitting ? ' btn-loading' : ''}`}
                disabled={state.submitting || state.deetsLoading}
                aria-busy={state.submitting || state.deetsLoading}
              >
                {state.deetsLoading ? 'Loading…' : state.submitting ? 'Processing…' : 'Deposit Now'}
              </button>
            )}
          </form>

          {state.clientSecret && (
            <div className="form-group stripe-element-group">
              <label>Payment Details</label>
              <StripeSection
                key={state.clientSecret}
                elementsOptions={elementsOptions}
                stripeFormRef={stripeFormRef}
                onPaymentError={handleStripeError}
                returnUrl={lockedReturnUrlRef.current}
                customerFullname={state.customerFullname}
                customerEmail={state.customerEmail}
              />
            </div>
          )}

          {state.stripeError && (
            <p className="submit-message error" role="alert">{state.stripeError}</p>
          )}

          {state.clientSecret && (
            <button
              type="button"
              className={`deposit-button${state.validating ? ' btn-loading' : ''}`}
              onClick={handleOpenSubModal}
              disabled={state.validating}
              aria-busy={state.validating}
            >
              {state.validating ? 'Validating…' : 'Continue to Confirm'}
            </button>
          )}

          {state.clientSecret && (
            <p className="security-note">
              🔒 Your payment information is encrypted and secure
            </p>
          )}
        </div>
      </div>

      <PaymentSubModal
        isOpen={state.showSubModal}
        onClose={handleCloseSubModal}
        onStripeConfirm={handleStripeConfirm}
        paymentData={paymentData}
      />
    </div>
  );
}

export default memo(DepositModal);
